import { FormEvent, useState } from "react"
import { useForm } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { FieldError } from "@/components/ui/field-error"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { PromotionTargetPicker } from "@/features/staff/promotions/promotion-target-picker"
import {
  PROMOTION_PATHS,
  promotionRequestComplete,
  promotionValueLabelKey,
  scopeNeedsTargets,
  tPromotion,
  type MenuCategoryOption,
  type MenuItemOption,
  type Promotion,
  type PromotionOption,
} from "@/features/staff/promotions/promotions"

type Props = {
  promotion?: Promotion
  kinds: PromotionOption[]
  scopes: PromotionOption[]
  items: MenuItemOption[]
  categories: MenuCategoryOption[]
  maxPercentage: number
  onDone?: () => void
}

type FormShape = {
  name: string
  kind: string
  scope: string
  value: string
  max_discount_amount: string
  starts_on: string
  ends_on: string
  priority: string
  target_ids: number[]
}

/**
 * Writing a standing price rule.
 *
 * **This keeps an explicit submit button**, and the "Updates vs creates" rule is
 * why: kind, value, scope, ceiling and dates only mean anything together. A rule
 * that committed field by field would be live and wrong between two keystrokes —
 * "20" saved before the operator changed the kind from percentage to pesos is a
 * different promotion than either of the two they had in mind. The one field
 * that does commit inline is `status`, and it lives on the card rather than here.
 *
 * The same component writes and edits, because the two forms are the same set of
 * decisions. `promotion` present means PATCH; absent means POST.
 *
 * A new rule is created as a draft and the form never offers `status`: an
 * unreviewed rule that changes no price is the safe starting point, and turning
 * it on is a second, deliberate act.
 */
export function PromotionForm({
  promotion,
  kinds,
  scopes,
  items,
  categories,
  maxPercentage,
  onDone,
}: Props) {
  const editing = Boolean(promotion)
  const [targetIds, setTargetIds] = useState<number[]>(promotion?.targetIds ?? [])

  const { data, setData, post, patch, errors, processing } = useForm<FormShape>({
    name: promotion?.name ?? "",
    kind: promotion?.kind ?? "percentage",
    scope: promotion?.scope ?? "bill",
    value: promotion?.value ?? "",
    max_discount_amount: promotion?.maxDiscountAmount ?? "",
    starts_on: promotion?.startsOn ?? "",
    ends_on: promotion?.endsOn ?? "",
    priority: String(promotion?.priority ?? 0),
    target_ids: promotion?.targetIds ?? [],
  })

  // `record.errors.to_hash(true)` keys a model-level error under `base`, which is
  // not a field on this form and so is not in `useForm`'s inferred error map.
  // Dropping it would swallow every refusal that is not about one input — the
  // target-sync failure the controller reports there, for one.
  const baseError = (errors as Record<string, string | string[] | undefined>).base

  const complete = promotionRequestComplete({
    name: data.name,
    kind: data.kind,
    scope: data.scope,
    value: data.value,
    targetIds,
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!complete) return

    const options = { preserveScroll: true, onSuccess: () => onDone?.() }

    if (promotion) {
      patch(PROMOTION_PATHS.update(promotion.id), options)
    } else {
      post(PROMOTION_PATHS.create, options)
    }
  }

  // Changing the scope discards a selection made for the other catalog: a dish
  // id kept across a switch to `categories` would be posted as a category id and
  // silently dropped by the server's own tenant lookup.
  const changeScope = (scope: string) => {
    setData("scope", scope)
    if (!scopeNeedsTargets(scope) || scope !== promotion?.scope) {
      setTargetIds([])
      setData("target_ids", [])
    }
  }

  const changeTargets = (ids: number[]) => {
    setTargetIds(ids)
    setData("target_ids", ids)
  }

  const testId = editing ? `promotion-edit-${promotion?.id}` : "promotion-new"

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${testId}-form`}>
      <FieldError error={baseError} />

      <div className="space-y-2">
        <Label htmlFor={`${testId}-name`}>{tPromotion("name_label")}</Label>
        <Input
          id={`${testId}-name`}
          value={data.name}
          onChange={(event) => setData("name", event.target.value)}
          placeholder={tPromotion("name_placeholder")}
          data-testid={`${testId}-name`}
          required
        />
        <FieldError error={errors.name} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${testId}-kind`}>{tPromotion("kind_label")}</Label>
          <Select value={data.kind} onValueChange={(value) => setData("kind", value)}>
            <SelectTrigger id={`${testId}-kind`} className="w-full" data-testid={`${testId}-kind`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {kinds.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  data-testid={`${testId}-kind-option-${option.value}`}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError error={errors.kind} />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${testId}-value`}>{tPromotion(promotionValueLabelKey(data.kind))}</Label>
          <Input
            id={`${testId}-value`}
            type="number"
            inputMode="decimal"
            min="0"
            max={data.kind === "percentage" ? String(maxPercentage) : undefined}
            step="0.01"
            value={data.value}
            onChange={(event) => setData("value", event.target.value)}
            className="tabular-nums"
            data-testid={`${testId}-value`}
            required
          />
          <FieldError error={errors.value} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${testId}-scope`}>{tPromotion("scope_label")}</Label>
        <Select value={data.scope} onValueChange={changeScope}>
          <SelectTrigger id={`${testId}-scope`} className="w-full" data-testid={`${testId}-scope`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {scopes.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                data-testid={`${testId}-scope-option-${option.value}`}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError error={errors.scope} />
      </div>

      <PromotionTargetPicker
        scope={data.scope}
        items={items}
        categories={categories}
        selectedIds={targetIds}
        onChange={changeTargets}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${testId}-starts-on`}>{tPromotion("starts_on_label")}</Label>
          <Input
            id={`${testId}-starts-on`}
            type="date"
            value={data.starts_on}
            onChange={(event) => setData("starts_on", event.target.value)}
            className="tabular-nums"
            data-testid={`${testId}-starts-on`}
          />
          <FieldError error={errors.starts_on} />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${testId}-ends-on`}>{tPromotion("ends_on_label")}</Label>
          <Input
            id={`${testId}-ends-on`}
            type="date"
            value={data.ends_on}
            onChange={(event) => setData("ends_on", event.target.value)}
            className="tabular-nums"
            data-testid={`${testId}-ends-on`}
          />
          <FieldError error={errors.ends_on} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${testId}-max-discount`}>{tPromotion("max_discount_label")}</Label>
          <Input
            id={`${testId}-max-discount`}
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={data.max_discount_amount}
            onChange={(event) => setData("max_discount_amount", event.target.value)}
            placeholder={tPromotion("max_discount_placeholder")}
            className="tabular-nums"
            data-testid={`${testId}-max-discount`}
          />
          <p className="text-muted-foreground text-xs">{tPromotion("max_discount_hint")}</p>
          <FieldError error={errors.max_discount_amount} />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${testId}-priority`}>{tPromotion("priority_label")}</Label>
          <Input
            id={`${testId}-priority`}
            type="number"
            step="1"
            value={data.priority}
            onChange={(event) => setData("priority", event.target.value)}
            className="tabular-nums"
            data-testid={`${testId}-priority`}
          />
          <p className="text-muted-foreground text-xs">{tPromotion("priority_hint")}</p>
          <FieldError error={errors.priority} />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {onDone && (
          <Button type="button" variant="outline" onClick={onDone} data-testid={`${testId}-cancel`}>
            {tPromotion("cancel")}
          </Button>
        )}
        <Button type="submit" disabled={processing || !complete} data-testid={`${testId}-submit`}>
          {editing ? tPromotion("save") : tPromotion("create")}
        </Button>
      </div>
    </form>
  )
}
